import { useState } from 'react';
import { ListTodo } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/Button';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useTaskTemplates } from '../../hooks/queries/useTaskTemplates';
import { useTaskTags } from '../../hooks/queries/useTaskTags';
import { useColorMap } from '../../hooks/queries/useEventTypes';
import { PRIORITY_META } from './taskHelpers';
import type { TaskTemplate } from '../../services/tasks/taskTemplateService';

export function TaskTemplatePicker({
  compact = false,
  onSelect,
}: {
  compact?: boolean;
  onSelect: (template: TaskTemplate) => void;
}) {
  const [open, setOpen] = useState(false);
  const { data: templates = [], isLoading } = useTaskTemplates(true);
  const { data: tags = [] } = useTaskTags();
  const { tags: showTags, priority: showPriority } = useEffectiveTaskFields();
  const tagsById = new Map(tags.map((t) => [t.id, t]));
  const colors = useColorMap();

  if (isLoading || templates.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {compact ? (
          <button
            type="button"
            title="Créer depuis une tâche type"
            className="flex-shrink-0 rounded-md px-2 py-1 text-xs text-cime-text-muted hover:bg-neutral-100 hover:text-cime-primary"
          >
            Sélectionner
          </button>
        ) : (
          <Button type="button" variant="secondary" size="sm">
            <ListTodo className="w-3.5 h-3.5" />
            Sélectionner
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        data-cime-modal
        className="w-72 p-1 rounded-lg border-cime-border bg-white shadow-lg"
      >
        <p className="px-2.5 py-1.5 text-[11px] uppercase tracking-wide text-cime-text-muted">
          Tâches types
        </p>
        <div className="max-h-64 overflow-y-auto">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                onSelect(t);
                setOpen(false);
              }}
              className="flex w-full flex-col items-start gap-0.5 px-2.5 py-1.5 rounded text-left hover:bg-[#fbfaf7]"
            >
              <span className="flex items-center gap-1.5 text-sm text-cime-text-primary">
                {colors[t.id] ? (
                  <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: colors[t.id] }} aria-hidden />
                ) : null}
                {t.content}
              </span>
              <span className="flex flex-wrap items-center gap-1">
                {showPriority && t.priority !== 3 && (
                  <span className={`text-[10px] px-1 rounded border ${PRIORITY_META[t.priority]?.className ?? ''}`}>
                    {PRIORITY_META[t.priority]?.label}
                  </span>
                )}
                {t.estimated_duration_minutes != null && (
                  <span className="text-[10px] text-cime-text-muted">
                    {t.estimated_duration_minutes} min
                  </span>
                )}
                {showTags && t.tag_ids.map((id) => {
                  const tag = tagsById.get(id);
                  if (!tag) return null;
                  return (
                    <span
                      key={id}
                      className="text-[10px] px-1 rounded-full text-white"
                      style={{ backgroundColor: tag.color }}
                    >
                      {tag.name}
                    </span>
                  );
                })}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
